// src/encounter/flag-add-dialog.tsx
// Add-flag picker — presentational. Composes `AddSurface` (anchored rail popover or centred DS
// Modal, chosen by where it is rendered) + a grouped category list. Categories come from the host
// (useFlagCategories, filtered by `offerableFlagGroups`). Pure: no hooks beyond local selection
// state, no network — so it is Storybook- and test-friendly.
//
// EXPLICIT COMMIT (encounter owner ruling, 1 Nov 2026, "chart rail + form fields"): a row tap only
// SELECTS a flag type; the Cancel / "Add flag" footer commits it. The picker used to save on the
// row tap, which wrote a flag to the patient's record on a single mis-tap with no confirm step.
import { useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import { AddSurface } from './add-surface';
import type { FlagCategoryGroup, FlagCategory } from '@/types/flags-api';
import { QuietLoading } from '@/shared/ui/components/quiet-loading';

export interface FlagAddDialogProps {
  open: boolean;
  groups: FlagCategoryGroup[];
  loading?: boolean;
  saving?: boolean;
  /** Runs on the explicit "Add flag" commit, never on a row tap. */
  onPick: (category: FlagCategory) => void;
  onClose: () => void;
}

/** Outer gate + remount per open, so a reopened picker never carries the last selection. */
export function FlagAddDialog(props: FlagAddDialogProps) {
  if (!props.open) return null;
  return <FlagAddDialogForm {...props} />;
}

function rowKey(groupName: string | null | undefined, c: FlagCategory): string {
  return `${groupName ?? 'ungrouped'}-${c.code}`;
}

function FlagAddDialogForm({ groups, loading, saving, onPick, onClose }: FlagAddDialogProps) {
  const [selected, setSelected] = useState<{ key: string; category: FlagCategory } | null>(null);
  return (
    <AddSurface
      open
      onClose={onClose}
      title="Add flag"
      commit={{
        run: () => {
          if (selected) onPick(selected.category);
        },
        label: 'Add flag',
        text: saving ? 'Adding…' : 'Add flag',
        disabled: !selected,
        busy: saving,
      }}
    >
      {/* maxHeight 360: no DS token (LIST — arbitrary px). The list look (group label, message, 32 tall rows) is
          `[data-slot="flag-add-*"]` in src/styles/showcase/encounter-shell.css (design EncCoreRailAddList). */}
      <div data-slot="flag-add-list" className="max-h-90 overflow-y-auto">
        {loading && <QuietLoading label="Loading flags…" />}
        {!loading && groups.length === 0 && <p data-slot="flag-add-message">No flags available.</p>}
        {groups.map((g) => (
          <section key={g.categoryName ?? 'ungrouped'}>
            <p data-slot="flag-add-group">{g.categoryName ?? 'Other'}</p>
            {(g.flagResponse ?? []).map((c) => {
              const key = rowKey(g.categoryName, c);
              const isSelected = selected?.key === key;
              return (
                // DS-only (PROD-1370): an option is a DS `Button` (full-width, left-aligned), not a raw
                // `<button all:unset>`. It is a single-choice SELECT now, so it states its pressed
                // state (`aria-pressed`) and takes the secondary variant while selected.
                <Button
                  key={key}
                  data-slot="flag-add-row"
                  variant={isSelected ? 'secondary' : 'tertiary'}
                  size="sm"
                  aria-pressed={isSelected}
                  disabled={saving}
                  onClick={() => setSelected({ key, category: c })}
                  fullWidth
                  className="justify-start"
                >
                  {/* `display` is nullable on the wire; fall back to the code so the row is identifiable. */}
                  {c.display ?? String(c.code ?? '-')}
                </Button>
              );
            })}
          </section>
        ))}
      </div>
    </AddSurface>
  );
}
